import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { dashboardApi } from '../../../shared/lib/api'
import { StatCard, Card, Badge, LoadingSpinner, Button } from '../../../shared/components/UI'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { BellOff } from 'lucide-react'
import clsx from 'clsx'

const STATUS_BADGE = {
  created: { v: 'blue', l: 'Created' },
  in_progress: { v: 'amber', l: 'In Progress' },
  completed: { v: 'green', l: 'Completed' }
}


export default function AdminDashboard() {
  const navigate = useNavigate()
  const { data: statsRes, isLoading, refetch } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: () => dashboardApi.adminStats().then(res => res.data.data),
    refetchInterval: 10000
  })

  if (isLoading) return <div className="p-12 text-center"><LoadingSpinner /></div>

  const stats = statsRes || {
    totalOrders: 0,
    activeUnits: 0,
    machineStatus: [],
    recentActivity: [],
    units: [],
    liveAlerts: [],
    productionStats: { trend: [] }
  }

  const activePositions = stats.machineStatus?.find(s => s.status === 'active')?.count || 0
  const inactivePositions = stats.machineStatus?.find(s => s.status === 'inactive')?.count || 0

  const handleDismiss = async (id) => {
    try {
      await dashboardApi.acknowledgeNotification(id)
      refetch()
    } catch (err) {
      console.error('Failed to dismiss alert:', err)
    }
  }

  return (
    <div>
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          label="Active Units"
          value={stats.activeUnits}
          sub="Operating now"
          subColor="text-teal-600"
          accent="brand"
        />
        <StatCard
          label="Active Positions"
          value={activePositions}
          sub={`${inactivePositions} positions inactive`}
          subColor={inactivePositions > 0 ? "text-amber-500" : "text-gray-400"}
          accent="amber"
        />
        <StatCard
          label="Cutting Batches"
          value={stats.cuttingStats?.cuttingBatches || 0}
          sub="Grouped & Ready"
          subColor="text-brand-600"
          accent="brand"
        />
        <StatCard
          label="Unbatched Logs"
          value={stats.cuttingStats?.unbatchedLogs || 0}
          sub="Pending Batching"
          subColor={stats.cuttingStats?.unbatchedLogs > 0 ? "text-amber-500" : "text-gray-400"}
          accent="amber"
        />
      </div>

      {/* Middle row: Analytics & Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        {/* Production Trend Chart */}
        <div className="lg:col-span-2">
          <Card
            title="Production Trend"
            action={<Badge variant="blue">Daily Output (KG)</Badge>}
            className="h-full"
          >
            <div className="h-[300px] w-full pt-4">
              {stats.productionStats?.trend?.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={stats.productionStats.trend}>
                    <defs>
                      <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#1D6FA8" stopOpacity={0.8} />
                        <stop offset="100%" stopColor="#1D6FA8" stopOpacity={0.3} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                    <XAxis
                      dataKey="date"
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748B', fontSize: 10 }}
                      dy={10}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: '#64748B', fontSize: 10 }}
                      width={35}
                    />
                    <Tooltip
                      cursor={{ fill: '#F1F5F9' }}
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                    />
                    <Legend iconType="circle" wrapperStyle={{ paddingTop: '20px', fontSize: '11px', fontWeight: 600 }} />
                    <Bar name="Cutting Output" dataKey="cutting" fill="url(#barGradient)" radius={[4, 4, 0, 0]} barSize={20} />
                    <Bar name="Finished Production" dataKey="production" fill="#0D9488" radius={[4, 4, 0, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-gray-400 space-y-2">
                  <div className="p-4 bg-gray-50 rounded-full">📊</div>
                  <p className="text-xs font-medium">No production data for this period</p>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Live Alerts */}
        <Card title="Live Alerts" className="flex flex-col h-full">
          <div className="space-y-3 flex-1 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
            {(!stats.liveAlerts || stats.liveAlerts.length === 0) ? (
              <div className="flex flex-col items-center justify-center h-full p-6 text-center bg-gradient-to-b from-gray-50/50 to-white rounded-2xl border border-dashed border-gray-200 m-2">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mb-4 shadow-inner ring-4 ring-white">
                  <BellOff size={24} className="text-green-500" />
                </div>
                <h4 className="text-sm font-black text-gray-800 mb-1 tracking-tight">All caught up!</h4>
                <p className="text-[11px] text-gray-500 max-w-[180px] leading-relaxed font-medium">
                  There are no new alerts or notifications at the moment.
                </p>
              </div>
            ) : (
              stats.liveAlerts.map(alert => (
                <div key={alert.id} className="group relative rounded-xl p-3 bg-white border border-gray-100 hover:border-brand-200 hover:shadow-md transition-all animate-in slide-in-from-right-2">
                  <div className="flex items-start gap-3">
                    <div className={clsx(
                      "w-2 h-2 mt-1.5 rounded-full flex-shrink-0 animate-pulse",
                      alert.type === 'variance_alert' ? "bg-red-500" : "bg-brand-500"
                    )} />
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start mb-1 leading-tight">
                        <p className="text-sm font-bold text-gray-800 truncate">{alert.title}</p>
                      </div>
                      <p className="text-[11px] text-gray-500 leading-relaxed mb-2 line-clamp-2">{alert.message}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          {alert.unit && <Badge variant="navy">{alert.unit.name}</Badge>}
                          <span className="text-[10px] text-gray-400 font-medium">
                            {new Date(alert.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        </div>
                        <button
                          onClick={() => handleDismiss(alert.id)}
                          className="bg-brand-50 text-brand-700 text-[10px] font-bold px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="mt-4 pt-4 border-t border-gray-50">
            <Button variant="secondary" size="sm" className="w-full text-[10px] h-8 font-bold" onClick={() => navigate('/admin/notifications')}>
              View all notifications →
            </Button>
          </div>
        </Card>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Recent Activity */}
        <Card title="Recent Activity" bodyClass="!p-0" className="lg:col-span-2">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>User</th>
                  <th>Resource</th>
                  <th>ID</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {(!stats.recentActivity || stats.recentActivity.length === 0) && (
                  <tr><td colSpan={5} className="text-center py-8 text-gray-400 text-sm">No activity recorded yet</td></tr>
                )}
                {(stats.recentActivity || []).map((log) => (
                  <tr key={log.id}>
                    <td>
                      <Badge variant="gray">{log.action}</Badge>
                    </td>
                    <td className="font-medium text-gray-700">{log.user?.name || 'System'}</td>
                    <td className="text-gray-500 text-xs">{log.entity_type}</td>
                    <td className="td-mono text-[10px]">{log.entity_id?.substring(0, 8)}...</td>
                    <td className="text-xs text-gray-400">{new Date(log.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  )
}
